import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Input,
  Radio,
  Space,
  Image,
  message,
  Card,
  Tag,
  Table
} from 'antd';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomButton from '@/common/CustomButton';
import truck from '@/features/generateMrn/components/images/truck-delivery-done.gif';
import { useDispatch, useSelector } from 'react-redux';
import { history } from 'app/history';
import Layout from '@/common/Layout';
import { useParams } from 'react-router-dom';
import { getAllQCMaterials, damage_report_qc } from '../../redux/slice';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

const { TextArea } = Input;

const STATUS_TAG_CONFIG = {
  Approved: { color: 'success', icon: <CheckCircleOutlined /> },
  Rejected: { color: 'error', icon: <CloseCircleOutlined /> },
  'QA Pending': { color: 'warning', icon: <ClockCircleOutlined /> }
};

const renderStatusTag = (status, label, key) => {
  const { color = 'default', icon = <QuestionCircleOutlined /> } =
    STATUS_TAG_CONFIG[status] || {};
  return (
    <Tag color={color} icon={icon} key={key} style={{ marginBottom: 4 }}>
      {label}
    </Tag>
  );
};

const DamageReportPage = ({ loading }) => {
  const params = useParams();
  const dispatch = useDispatch();
  const { allqcMaterials } = useSelector((state) => state.qc);
  const [form] = Form.useForm();

  const [rowState, setRowState] = useState({});
  const [truckShow, setTruckShow] = useState(false);

  useEffect(() => {
    handleGetAllMaterials();
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      invoice_no: localStorage.getItem('invoice_num'),
      material_code: localStorage.getItem('material_code'),
      material_name: localStorage.getItem('material_name')
    });
  }, []);

  const handleGetAllMaterials = () => {
    dispatch(getAllQCMaterials({ id: params.id }));
  };

  const batch_containers = allqcMaterials?.damages?.damaged_containers || [];
  const pendingContainers = batch_containers.filter(
    (c) => c?.status === 'QA Pending'
  );
  const processedContainers = batch_containers.filter(
    (c) => c?.status !== 'QA Pending'
  );

  const handleRowCommentChange = (containerNumber, value) => {
    setRowState((prev) => ({
      ...prev,
      [containerNumber]: { ...prev[containerNumber], comments: value }
    }));
  };

  const handleRowDecisionChange = (containerNumber, value) => {
    setRowState((prev) => ({
      ...prev,
      [containerNumber]: { ...prev[containerNumber], is_accepted: value }
    }));
  };

  const handleClickSubmit = () => {
    const missing = pendingContainers.filter(
      (c) => !rowState[c.container_number]?.is_accepted
    );
    if (missing.length > 0) {
      message.error(
        'Please select Approved/Reject for every container before submitting'
      );
      return;
    }

    const damage_containers = pendingContainers.map((c) => {
      const decision = rowState[c.container_number]?.is_accepted;
      return {
        container_number: c.container_number,
        comments: rowState[c.container_number]?.comments || '',
        is_accepted: decision,
        status: decision === 'yes' ? 'Approved' : 'Rejected',
        damaged_quantity: c.damaged_quantity
      };
    });

    setTruckShow(true);
    dispatch(
      damage_report_qc({
        document_id: params.id,
        damage_containers
      })
    )
      .then((response) => {
        if (response?.payload?.success) {
          message.success(response?.payload?.message);
          history.back();
        }
      })
      .finally(() => {
        setTruckShow(false);
      });
  };

  const columns = [
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: (_, record) => {
        const decision = rowState[record.container_number]?.is_accepted;
        const liveStatus =
          decision === 'yes'
            ? 'Approved'
            : decision === 'no'
            ? 'Rejected'
            : 'QA Pending';
        const label = liveStatus === 'QA Pending' ? 'Pending' : liveStatus;
        return renderStatusTag(liveStatus, label);
      }
    },
    {
      title: 'Container Number',
      dataIndex: 'container_number',
      key: 'container_number',
      width: 200
    },
    {
      title: 'Damaged Quantity',
      dataIndex: 'damaged_quantity',
      key: 'damaged_quantity',
      width: 160,
      render: (qty) => (qty ? qty : '-')
    },
    {
      title: 'Comments',
      key: 'comments',
      render: (_, record) => (
        <TextArea
          placeholder="Please enter comments"
          value={rowState[record.container_number]?.comments || ''}
          onChange={(e) =>
            handleRowCommentChange(record.container_number, e.target.value)
          }
          autoSize={{ minRows: 1, maxRows: 3 }}
        />
      )
    },
    {
      title: 'Decision',
      key: 'decision',
      width: 220,
      render: (_, record) => (
        <Radio.Group
          value={rowState[record.container_number]?.is_accepted}
          onChange={(e) =>
            handleRowDecisionChange(record.container_number, e.target.value)
          }
          className={styles.customRadio}
        >
          <Radio value="yes">Approved</Radio>
          <Radio value="no">Reject</Radio>
        </Radio.Group>
      )
    }
  ];

  return (
    <Layout sider={false}>
      <Form form={form} layout="vertical">
        <Card
          title="APPROVAL FOR DAMAGES REPORTED"
          className={styles.CustomPanel}
        >
          <Row gutter={24}>
            <Col xs={24} xl={8} span={8}>
              <FormItem
                label="Material Code"
                name="material_code"
                className={styles.customLable}
              >
                <Input placeholder="Enter Material code" disabled />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8}>
              <FormItem
                label="Material Name"
                name="material_name"
                className={styles.customLable}
              >
                <Input placeholder="Enter Material name" disabled />
              </FormItem>
            </Col>
            <Col xs={24} xl={8} span={8}>
              <FormItem
                label="Invoice Number"
                name="invoice_no"
                className={styles.customLable}
              >
                <Input placeholder="Enter Invoice number" disabled />
              </FormItem>
            </Col>
          </Row>
        </Card>

        <Card title="DAMAGED CONTAINERS" className={styles.CustomPanel}>
          {pendingContainers.length ? (
            <Table
              columns={columns}
              dataSource={pendingContainers}
              rowKey={(record) => record.container_number}
              pagination={false}
              bordered
              size="small"
            />
          ) : (
            <p style={{ marginBottom: 0 }}>
              No pending containers for approval.
            </p>
          )}

          {processedContainers.length ? (
            <>
              <br />
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                Already Processed
              </div>
              {processedContainers.map((item, index) =>
                renderStatusTag(
                  item.status,
                  `${item.container_number} ${
                    item.damaged_quantity ? `(${item.damaged_quantity})` : ''
                  }`,
                  index
                )
              )}
            </>
          ) : null}
        </Card>

        <br />
        <FormItem className="text-center">
          <Space>
            {truckShow ? (
              <Image
                src={truck}
                preview={false}
                style={{ width: '100px !important', height: '100px' }}
              />
            ) : pendingContainers.length ? (
              <CustomButton
                type="primary"
                onClick={handleClickSubmit}
                loading={loading}
              >
                Submit
              </CustomButton>
            ) : null}
          </Space>
        </FormItem>
      </Form>
    </Layout>
  );
};
export default DamageReportPage;
